import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {CommonConstants} from '@coreConstant';
import {GoogleAddressModel, PaginationModel} from '@coreModels';
import {ToastService} from './toast.service';
import {isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  constructor(
    private toastService: ToastService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
  }

  /**
   * Common Function for displaying error image
   * @param event
   * @param image
   */
  public getErrorImage(event: any, image?: any) {
    image == '' || image == undefined
      ? (event.target.src = CommonConstants.NO_IMAGE_PRODUCT)
      : (event.target.src =
        CommonConstants.IMAGE_KIT_ENDPOINT +
        '/ASSETS/GENERAL/IMAGES/' +
        image);
  }

  public addressHandler(address: any): GoogleAddressModel | null {
    if (!address?.formatted_address || !address?.address_components) {
      return null;
    }

    let addressModel = new GoogleAddressModel();
    let streetAddress = '';
    let isStreet = false;

    // Create a map to store components by type
    const componentMap = new Map();

    // First, organize all components by type
    address.address_components.forEach((component: any) => {
      if (component.types && component.types.length > 0) {
        component.types.forEach((type: string) => {
          componentMap.set(type, component);
        });
      }
    });

    // Handle subpremise and street number first
    const subpremise = componentMap.get('subpremise');
    const streetNumber = componentMap.get('street_number');

    if (subpremise) {
      // Google writes the unit lowercase ('unit 2'): capitalise it like the rest of the address
      streetAddress += subpremise.long_name.replace(/^\w/, (c: string) => c.toUpperCase()) + '/';
    }

    if (streetNumber) {
      isStreet = true;
      streetAddress += streetNumber.long_name;
    }

    // Handle premise
    const premise = componentMap.get('premise');
    if (premise) {
      streetAddress += (streetAddress ? ',' : '') + ' ';
    }

    // Handle route (street name)
    const route = componentMap.get('route');
    if (route) {
      addressModel.address_line_1 = streetAddress
        ? `${streetAddress}, ${route.long_name}`
        : route.long_name;
    }

    // Map remaining components
    const mappings = {
      'sublocality_level_3': (comp: any) => addressModel.address_line_2 = comp.long_name,
      'postal_code': (comp: any) => addressModel.post_code = comp.long_name,
      'locality': (comp: any) => addressModel.suburb = comp.long_name,
      'administrative_area_level_1': (comp: any) => addressModel.state_region = comp.short_name,
      'administrative_area_level_2': (comp: any) => addressModel.town_city = comp.long_name,
      'country': (comp: any) => addressModel.country = comp.long_name
    };

    // Apply mappings
    Object.entries(mappings).forEach(([type, handler]) => {
      const component = componentMap.get(type);
      if (component) {
        handler(component);
      }
    });

    // Set coordinates
    if (address.geometry?.location) {
      addressModel.lattitude = address.geometry.location.lat();
      addressModel.longitude = address.geometry.location.lng();
    }

    addressModel.isStreetAddress = isStreet;

    return addressModel;
  }

  /**
   * @description Common function for the get(listing) request
   * @param response
   */
  public processPaginationResponse<T>(response: any): PaginationModel<T> {
    const paginationModel = new PaginationModel<T>();
    const headers = response.headers;
    paginationModel.setHeaders(headers);
    paginationModel.data = response.body.data as T[]; // Use the generic type for the data
    return paginationModel;
  }

  /**
   * Creates a slug from the given string.
   * @param slug The input string to convert into a slug.
   * @returns A slugified string.
   */
  public createSlug(slug: string): string {
    // Regular expression to match allowed characters
    const lettersOnlyPattern = /[^\-\s\p{L}]+/gu; // Remove all characters except letters, hyphen, and spaces
    const spacesDuplicateUnderscorePattern = /[\-\s]+/g; // Remove duplicate dashes (-) and spaces

    // Use the appropriate pattern based on the isAllowNumber flag
    const pattern = lettersOnlyPattern;

    // Convert to lowercase, remove unwanted characters
    slug = slug.toLowerCase().replace(pattern, '');

    // Replace spaces or multiple dashes with a single dash
    slug = slug.replace(spacesDuplicateUnderscorePattern, '-');

    // Trim any leading or trailing dashes
    slug = slug.replace(/^-+|-+$/g, '');

    return slug;
  }

  /**
   * Common Function to get file name
   * @param str
   * @returns filename
   */
  getFileName(str: any, usefor = 'alt') {
    let fileName = '';
    if (str != undefined && str !== null && str !== '') {
      let newFileName = str.split('\\').pop().split('/').pop(); // Get file name
      newFileName = newFileName.split('/').slice(-1).join().split('.').shift(); // Remove extension
      fileName = newFileName.replace(/_/g, ' '); // Replace underscore(_) with dash(-)
    }
    if (usefor == 'title') {
      fileName = fileName.toUpperCase();
    }
    return fileName;
  }

  copyText(str: string, successMessage: string) {
    // Skip during server-side rendering
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const decodedText = decodeURIComponent(str);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Use Clipboard API if supported
      navigator.clipboard
        .writeText(decodedText)
        .then(() => {
          this.toastService.showSuccess(successMessage);
        })
        .catch((err) => {
          this.toastService.showError(
            'Failed to copy the link. Please try again.'
          );
        });
    } else {
      // Fallback for unsupported browsers
      try {
        const listener = (e: ClipboardEvent) => {
          e.clipboardData?.setData('text/plain', decodedText);
          e.preventDefault();
        };
        document.addEventListener('copy', listener);
        const successful = document.execCommand('copy');
        document.removeEventListener('copy', listener);

        if (successful) {
          this.toastService.showSuccess(successMessage);
        } else {
          this.toastService.showError(
            'Failed to copy the link. Please try again.'
          );
        }
      } catch (err) {
        this.toastService.showError(
          'Copy operation failed. Please try manually.'
        );
      }
    }
  }
}
